import { useCallback, useEffect, useState } from 'react';
import { api, type ComparacionReleases, type Release } from '../api';
import { Tabla } from './Tabla';
import {
  deltaFormateado,
  milisegundos,
  parPorDefecto,
  presentar,
  puntos,
  resumirFilas,
} from '../releases';

/**
 * Comparación entre dos releases.
 *
 * La detección de anomalías ya avisa cuando un despliegue rompe algo. Esto
 * responde la otra mitad de la pregunta: cómo viene un release, cuando nadie
 * avisó nada. Sin esta pantalla el silencio del detector es ambiguo — no se
 * distingue «no hubo regresión» de «no había con qué comparar».
 */

function Celda({
  comparacion,
  valor,
  delta,
}: {
  comparacion: { a: number; b: number; delta: number; veredicto: string; motivo: string; contrastada: boolean } | null;
  valor: (v: number) => string;
  /** El delta va en su propia unidad: puntos para una tasa, tiempo para el p95. */
  delta: (v: number) => string;
}) {
  if (!comparacion) return <td className="num tenue">—</td>;

  const p = presentar(comparacion.veredicto as never);
  return (
    <td className={`num veredicto-${p.clase}`} title={comparacion.motivo}>
      <span className="valores">
        {valor(comparacion.a)} → {valor(comparacion.b)}
      </span>
      <span className="delta">
        {p.simbolo} {deltaFormateado(comparacion.delta, delta)}
        {!comparacion.contrastada && comparacion.veredicto !== 'sin_datos' && (
          <em title="Sin contraste estadístico: desde un percentil ya calculado no se puede saber si la diferencia es ruido">
            *
          </em>
        )}
      </span>
    </td>
  );
}

export function Releases({
  desde,
  canal,
  onError,
}: {
  desde: string;
  canal: string;
  onError: (err: unknown) => void;
}) {
  const [releases, setReleases] = useState<Release[] | null>(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [comparacion, setComparacion] = useState<ComparacionReleases | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargarReleases = useCallback(async () => {
    try {
      const datos = await api.releases(desde, canal || undefined);
      setReleases(datos.releases);
      const par = parPorDefecto(datos.releases.map((r) => r.app_version));
      if (par) {
        setA(par.a);
        setB(par.b);
      } else {
        setA('');
        setB('');
        setComparacion(null);
      }
    } catch (err) {
      onError(err);
    }
  }, [desde, canal, onError]);

  useEffect(() => {
    void cargarReleases();
  }, [cargarReleases]);

  useEffect(() => {
    if (!a || !b || a === b) {
      setComparacion(null);
      setAviso(a && a === b ? 'Elige dos versiones distintas.' : null);
      return;
    }
    setAviso(null);
    let vigente = true;
    api
      .compararReleases(a, b, desde, canal || undefined)
      .then((datos) => {
        // Los selectores pueden cambiar mientras la petición viaja: sin esta
        // guarda, una respuesta lenta de un par anterior pisa a la actual.
        if (vigente) setComparacion(datos);
      })
      .catch(onError);
    return () => {
      vigente = false;
    };
  }, [a, b, desde, canal, onError]);

  if (!releases) return <p className="vacio">Cargando…</p>;

  if (releases.length === 0) {
    return (
      <section className="releases">
        <p className="vacio">
          No llegó ninguna versión en esta ventana. El release sale de{' '}
          <code>service.version</code>: si las aplicaciones no lo declaran al inicializar el SDK,
          acá no hay nada que comparar.
        </p>
      </section>
    );
  }

  if (releases.length === 1) {
    return (
      <section className="releases">
        <p className="vacio">
          Solo hay una versión con actividad en esta ventana ({releases[0]?.app_version}). Amplía
          la ventana para alcanzar el despliegue anterior.
        </p>
      </section>
    );
  }

  const filasProceso = comparacion?.filas.filter((f) => !f.paso) ?? [];

  return (
    <section className="releases">
      <div className="releases-cabecera">
        <h3>Comparar releases</h3>
        <div className="selectores">
          <label>
            <span>Base</span>
            <select value={a} onChange={(e) => setA(e.target.value)}>
              {releases.map((r) => (
                <option key={r.app_version} value={r.app_version}>
                  {r.app_version} · {r.ejecuciones} ejec
                </option>
              ))}
            </select>
          </label>
          <span className="flecha">→</span>
          <label>
            <span>Contra</span>
            <select value={b} onChange={(e) => setB(e.target.value)}>
              {releases.map((r) => (
                <option key={r.app_version} value={r.app_version}>
                  {r.app_version} · {r.ejecuciones} ejec
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {aviso && <p className="error">{aviso}</p>}

      {comparacion && (
        <>
          <p className={`resumen veredicto-${presentar(comparacion.veredicto).clase}`}>
            <strong>{presentar(comparacion.veredicto).etiqueta}.</strong>{' '}
            {resumirFilas(filasProceso.map((f) => f.veredicto))}
          </p>

          <Tabla className="comparacion">
            <thead>
              <tr>
                <th>Proceso y pasos</th>
                <th className="num">Ejecuciones</th>
                <th className="num">Tasa de error</th>
                <th className="num">Abandono</th>
                <th className="num">p95</th>
              </tr>
            </thead>
            <tbody>
              {comparacion.filas.map((f) => (
                <tr
                  key={`${f.proceso}|${f.paso}`}
                  className={`${f.paso ? 'fila-paso' : 'fila-proceso'} veredicto-${presentar(f.veredicto).clase}`}
                >
                  <td>
                    {f.paso ? (
                      <span className="paso">
                        {f.indice + 1}. {f.paso}
                      </span>
                    ) : (
                      <strong>{f.proceso}</strong>
                    )}
                  </td>
                  <td className="num tenue">
                    {f.a.ejecuciones} → {f.b.ejecuciones}
                  </td>
                  <Celda comparacion={f.error} valor={(v) => `${v}%`} delta={puntos} />
                  <Celda comparacion={f.abandono} valor={(v) => `${v}%`} delta={puntos} />
                  <Celda comparacion={f.latencia} valor={milisegundos} delta={milisegundos} />
                </tr>
              ))}
            </tbody>
          </Tabla>

          <p className="tenue">
            Las tasas se contrastan contra el azar: una diferencia que cabe dentro de la
            variabilidad esperable de esa cantidad de ejecuciones se marca como sin cambio, aunque
            los porcentajes se vean distintos. El p95 lleva <em>*</em> porque no admite ese
            contraste —desde un percentil ya calculado no se puede reconstruir la dispersión— y
            solo se le exige que el cambio sea grande: es indicativo, no probado.
          </p>
        </>
      )}
    </section>
  );
}
